import React, { FC, useState } from "react";
import PropTypes from "prop-types";
import { matchPath, useNavigate } from "react-router-dom";
import { List, ListProps, ListSubheader, IconButton } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore"; // Import the ExpandMore icon
import NavItem from "./NavItem";
import useDrawer from "../../hooks/useDrawer";

interface Item {
  path?: string;
  info?: React.ReactNode;
  data?: Item[];
  title: string;
}

interface NavSectionProps extends ListProps {
  data: Item[];
  path?: string;
  pathname: string;
  title: string;
}

const renderNavItems = ({
  depth = 0,
  data,
  pathname,
  drawerOpen,
}: {
  data: Item[];
  pathname: string;
  path?: string;
  depth?: number;
  drawerOpen: boolean;
}): JSX.Element => (
  <List disablePadding>
    {data.reduce(
      (acc, item) =>
        reduceChildRoutes({
          acc,
          item,
          pathname,
          depth,
          drawerOpen,
        }),
      []
    )}
  </List>
);

const reduceChildRoutes = ({
  acc,
  pathname,
  item,
  depth,
  drawerOpen,
}: {
  acc: JSX.Element[];
  pathname: string;
  item: Item;
  depth: number;
  drawerOpen: boolean;
}): Array<JSX.Element> => {
  const key = `${item.title}-${depth}`;
  const exactMatch = item.path
    ? !!matchPath(
        {
          path: item.path,
          end: true,
        },
        pathname
      )
    : false;

  if (item.data.length !== 0) {
    const partialMatch = item.path
      ? !!matchPath(
          {
            path: item.path,
            end: false,
          },
          pathname
        )
      : false;

    acc.push(
      <NavItem
        active={partialMatch}
        depth={depth}
        info={item.info}
        key={key}
        open={partialMatch}
        path={item.path}
        title={drawerOpen ? item.title : ""}
      >
        {renderNavItems({
          depth: depth + 1,
          data: item.data,
          pathname,
          drawerOpen,
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        active={exactMatch}
        depth={depth}
        info={item.info}
        key={key}
        path={item.path}
        title={drawerOpen ? item.title : ""}
      />
    );
  }

  return acc;
};

const NavSection: FC<NavSectionProps> = (props) => {
  const { data, pathname, title, path } = props;
  const { drawerOpen } = useDrawer();
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleSubMenuToggle = () => {
    setSubMenuOpen(!subMenuOpen);
  };

  const handleMenuClick = () => {
    if (data.length === 0) {
      navigate(path);
    } else {
      setSubMenuOpen(!subMenuOpen);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <List
        key={title}
        subheader={
          <ListSubheader
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: 'black',
              fontSize: '0.9rem',
              lineHeight: 2,
              fontWeight: 700,
              // textTransform: 'uppercase',
              cursor: "pointer", // data.length > 0 ? 'pointer' : 'default',
            }}
            onClick={handleMenuClick}
          >
            {title}
            {data.length > 0 && (
              <IconButton
                style={{ paddingRight: 0, paddingLeft: 0 }}
                onClick={handleSubMenuToggle}
              >
                <ExpandMoreIcon />
              </IconButton>
            )}
          </ListSubheader>
        }
      >
        {subMenuOpen && (
          <div style={{ marginLeft: "20px" }}>
            {renderNavItems({
              data,
              pathname,
              drawerOpen,
            })}
          </div>
        )}
      </List>
    </div>
  );
};

NavSection.propTypes = {
  data: PropTypes.array,
  pathname: PropTypes.string,
  title: PropTypes.string,
};

export default NavSection;
